import * as Icons from "@/components/ui/atoms/icons";
import { PUBLIC_ICON_URL } from "./constants";
import type { OAuthCatalogue } from "./types";

export const OAuthId = {
  Airtable: "arcade-airtable",
  Asana: "arcade-asana",
  Atlassian: "arcade-atlassian",
  Attio: "arcade-attio",
  Calendly: "arcade-calendly",
  Clickup: "arcade-clickup",
  Discord: "arcade-discord",
  Dropbox: "arcade-dropbox",
  Figma: "arcade-figma",
  Github: "arcade-github",
  Google: "arcade-google",
  Hubspot: "arcade-hubspot",
  Linkedin: "arcade-linkedin",
  Linear: "arcade-linear",
  Mailchimp: "arcade-mailchimp",
  MCP_OAuth2: "arcade-mcp-oauth2",
  Microsoft: "arcade-microsoft",
  Miro: "arcade-miro",
  Notion: "arcade-notion",
  OAuth2: "arcade-oauth2",
  Pagerduty: "arcade-pagerduty",
  Reddit: "arcade-reddit",
  Salesforce: "arcade-salesforce",
  Slack: "arcade-slack",
  Spotify: "arcade-spotify",
  Squareup: "arcade-squareup",
  Tesla: "arcade-tesla",
  Ticktick: "arcade-ticktick",
  Twitch: "arcade-twitch",
  X: "arcade-x",
  Zendesk: "arcade-zendesk",
  Zoho: "arcade-zoho",
  Zoom: "arcade-zoom",
} as const;

export const OAuthProviderId = {
  Airtable: "airtable",
  Asana: "asana",
  Atlassian: "atlassian",
  Attio: "attio",
  Calendly: "calendly",
  Clickup: "clickup",
  Discord: "discord",
  Dropbox: "dropbox",
  Figma: "figma",
  Github: "github",
  Google: "google",
  Hubspot: "hubspot",
  Linkedin: "linkedin",
  Linear: "linear",
  Mailchimp: "mailchimp",
  MCP_OAuth2: "mcp-oauth2",
  Microsoft: "microsoft",
  Miro: "miro",
  Notion: "notion",
  OAuth2: "oauth2",
  Pagerduty: "pagerduty",
  Reddit: "reddit",
  Salesforce: "salesforce",
  Slack: "slack",
  Spotify: "spotify",
  Squareup: "squareup",
  Tesla: "tesla",
  Ticktick: "ticktick",
  Twitch: "twitch",
  X: "x",
  Zendesk: "zendesk",
  Zoho: "zoho",
  Zoom: "zoom",
} as const;

/**
 * PREBUILT OAUTH PROVIDERS - A list of all OAuth providers that are prebuilt
 */
export const PREBUILT_OAUTH_PROVIDERS: OAuthCatalogue[] = [
  {
    id: OAuthId.Airtable,
    provider_id: OAuthProviderId.Airtable,
    name: "Airtable",
    description: "Authorize tools and agents with Airtable",
    publicIconUrl: `${PUBLIC_ICON_URL}/airtable.svg`,
    icon: Icons.Airtable,
    docs: "https://docs.arcade.dev/home/auth-providers/airtable",
  },
  {
    id: OAuthId.Asana,
    provider_id: OAuthProviderId.Asana,
    name: "Asana",
    description: "Authorize tools and agents with Asana",
    publicIconUrl: `${PUBLIC_ICON_URL}/asana.svg`,
    icon: Icons.Asana,
    docs: "https://docs.arcade.dev/home/auth-providers/asana",
  },
  {
    id: OAuthId.Atlassian,
    provider_id: OAuthProviderId.Atlassian,
    name: "Atlassian",
    description: "Authorize tools and agents with Atlassian",
    publicIconUrl: `${PUBLIC_ICON_URL}/atlassian.svg`,
    icon: Icons.Atlassian,
    docs: "https://docs.arcade.dev/home/auth-providers/atlassian",
  },
  {
    id: OAuthId.Attio,
    provider_id: OAuthProviderId.Attio,
    name: "Attio",
    description: "Authorize tools and agents with Attio CRM",
    publicIconUrl: `${PUBLIC_ICON_URL}/attio.svg`,
    icon: Icons.Attio,
    docs: "https://docs.arcade.dev/home/auth-providers/attio",
  },
  {
    id: OAuthId.Calendly,
    provider_id: OAuthProviderId.Calendly,
    name: "Calendly",
    description: "Authorize tools and agents with Calendly",
    publicIconUrl: `${PUBLIC_ICON_URL}/calendly.svg`,
    icon: Icons.Calendly,
    docs: "https://docs.arcade.dev/home/auth-providers/calendly",
  },
  {
    id: OAuthId.Clickup,
    provider_id: OAuthProviderId.Clickup,
    name: "ClickUp",
    description: "Authorize tools and agents with ClickUp",
    publicIconUrl: `${PUBLIC_ICON_URL}/clickup.svg`,
    icon: Icons.Clickup,
    docs: "https://docs.arcade.dev/home/auth-providers/clickup",
  },
  {
    id: OAuthId.Discord,
    provider_id: OAuthProviderId.Discord,
    name: "Discord",
    description: "Authorize tools and agents with Discord in a user's context",
    publicIconUrl: `${PUBLIC_ICON_URL}/discord.svg`,
    icon: Icons.Discord,
    docs: "https://docs.arcade.dev/home/auth-providers/discord",
  },
  {
    id: OAuthId.Dropbox,
    provider_id: OAuthProviderId.Dropbox,
    name: "Dropbox",
    description: "Authorize tools and agents with Dropbox",
    publicIconUrl: `${PUBLIC_ICON_URL}/dropbox.svg`,
    icon: Icons.Dropbox,
    docs: "https://docs.arcade.dev/home/auth-providers/dropbox",
  },
  {
    id: OAuthId.Figma,
    provider_id: OAuthProviderId.Figma,
    name: "Figma",
    description: "Authorize tools and agents with Figma",
    publicIconUrl: `${PUBLIC_ICON_URL}/figma.svg`,
    icon: Icons.Figma,
    docs: "https://docs.arcade.dev/home/auth-providers/figma",
  },
  {
    id: OAuthId.Github,
    provider_id: OAuthProviderId.Github,
    name: "GitHub",
    description:
      "Authorize tools and agents with GitHub, including private repositories",
    publicIconUrl: `${PUBLIC_ICON_URL}/github.svg`,
    icon: Icons.Github,
    docs: "https://docs.arcade.dev/home/auth-providers/github",
  },
  {
    id: OAuthId.Google,
    provider_id: OAuthProviderId.Google,
    name: "Google",
    description:
      "Authorize tools and agents with Google: Gmail, Calendar, YouTube, Drive, and more",
    publicIconUrl: `${PUBLIC_ICON_URL}/google.svg`,
    icon: Icons.Google,
    docs: "https://docs.arcade.dev/home/auth-providers/google",
  },
  {
    id: OAuthId.Hubspot,
    provider_id: OAuthProviderId.Hubspot,
    name: "HubSpot",
    description: "Authorize tools and agents with HubSpot",
    publicIconUrl: `${PUBLIC_ICON_URL}/hubspot.svg`,
    icon: Icons.Hubspot,
    docs: "https://docs.arcade.dev/home/auth-providers/hubspot",
  },
  {
    id: OAuthId.Linear,
    provider_id: OAuthProviderId.Linear,
    name: "Linear",
    description: "Authorize tools and agents with Linear",
    publicIconUrl: `${PUBLIC_ICON_URL}/linear.svg`,
    icon: Icons.Linear,
    docs: "https://docs.arcade.dev/home/auth-providers/linear",
  },
  {
    id: OAuthId.Linkedin,
    provider_id: OAuthProviderId.Linkedin,
    name: "LinkedIn",
    description: "Authorize tools and agents with LinkedIn",
    publicIconUrl: `${PUBLIC_ICON_URL}/linkedin.svg`,
    icon: Icons.Linkedin,
    docs: "https://docs.arcade.dev/home/auth-providers/linkedin",
  },
  {
    id: OAuthId.Mailchimp,
    provider_id: OAuthProviderId.Mailchimp,
    name: "Mailchimp",
    description: "Authorize tools and agents with Mailchimp",
    publicIconUrl: `${PUBLIC_ICON_URL}/mailchimp.svg`,
    icon: Icons.Mailchimp,
    docs: "https://docs.arcade.dev/home/auth-providers/mailchimp",
  },
  {
    id: OAuthId.Microsoft,
    provider_id: OAuthProviderId.Microsoft,
    name: "Microsoft",
    description: "Authorize tools and agents with Microsoft",
    publicIconUrl: `${PUBLIC_ICON_URL}/microsoft.svg`,
    icon: Icons.Microsoft,
    docs: "https://docs.arcade.dev/home/auth-providers/microsoft",
  },
  {
    id: OAuthId.Miro,
    provider_id: OAuthProviderId.Miro,
    name: "Miro",
    description: "Authorize tools and agents with Miro",
    publicIconUrl: `${PUBLIC_ICON_URL}/miro.svg`,
    icon: Icons.Miro,
    docs: "https://docs.arcade.dev/home/auth-providers/miro",
  },
  {
    id: OAuthId.Notion,
    provider_id: OAuthProviderId.Notion,
    name: "Notion",
    description: "Authorize tools and agents with Notion",
    publicIconUrl: `${PUBLIC_ICON_URL}/notion.svg`,
    icon: Icons.Notion,
    docs: "https://docs.arcade.dev/home/auth-providers/notion",
  },
  {
    id: OAuthId.Pagerduty,
    provider_id: OAuthProviderId.Pagerduty,
    name: "PagerDuty",
    description: "Authorize tools and agents with PagerDuty",
    publicIconUrl: `${PUBLIC_ICON_URL}/pagerduty.svg`,
    icon: Icons.Pagerduty,
    docs: "https://docs.arcade.dev/home/auth-providers/pagerduty",
  },
  {
    id: OAuthId.Reddit,
    provider_id: OAuthProviderId.Reddit,
    name: "Reddit",
    description: "Authorize tools and agents with Reddit",
    publicIconUrl: `${PUBLIC_ICON_URL}/reddit.svg`,
    icon: Icons.Reddit,
    docs: "https://docs.arcade.dev/home/auth-providers/reddit",
  },
  {
    id: OAuthId.Slack,
    provider_id: OAuthProviderId.Slack,
    name: "Slack",
    description: "Authorize tools and agents with Slack",
    publicIconUrl: `${PUBLIC_ICON_URL}/slack.svg`,
    icon: Icons.Slack,
    docs: "https://docs.arcade.dev/home/auth-providers/slack",
  },
  {
    id: OAuthId.Spotify,
    provider_id: OAuthProviderId.Spotify,
    name: "Spotify",
    description: "Authorize tools and agents with Spotify",
    publicIconUrl: `${PUBLIC_ICON_URL}/spotify.svg`,
    icon: Icons.Spotify,
    docs: "https://docs.arcade.dev/home/auth-providers/spotify",
  },
  {
    id: OAuthId.Squareup,
    provider_id: OAuthProviderId.Squareup,
    name: "Square",
    description: "Authorize tools and agents with Square",
    publicIconUrl: `${PUBLIC_ICON_URL}/square.svg`,
    icon: Icons.Square,
    docs: "https://docs.arcade.dev/home/auth-providers/squareup",
  },
  {
    id: OAuthId.Ticktick,
    provider_id: OAuthProviderId.Ticktick,
    name: "TickTick",
    description: "Authorize tools and agents with TickTick",
    publicIconUrl: `${PUBLIC_ICON_URL}/ticktick.svg`,
    icon: Icons.Ticktick,
    docs: "https://docs.arcade.dev/home/auth-providers/ticktick",
  },
  {
    id: OAuthId.Twitch,
    provider_id: OAuthProviderId.Twitch,
    name: "Twitch",
    description: "Authorize tools and agents with Twitch",
    publicIconUrl: `${PUBLIC_ICON_URL}/twitch.svg`,
    icon: Icons.Twitch,
    docs: "https://docs.arcade.dev/home/auth-providers/twitch",
  },
  {
    id: OAuthId.X,
    provider_id: OAuthProviderId.X,
    name: "X (Twitter)",
    description: "Authorize tools and agents with X (Twitter)",
    publicIconUrl: `${PUBLIC_ICON_URL}/x.svg`,
    icon: Icons.X,
    docs: "https://docs.arcade.dev/home/auth-providers/x",
  },
  {
    id: OAuthId.Zoho,
    provider_id: OAuthProviderId.Zoho,
    name: "Zoho",
    description: "Authorize tools and agents with Zoho",
    publicIconUrl: `${PUBLIC_ICON_URL}/zoho.svg`,
    icon: Icons.Zoho,
    docs: "https://docs.arcade.dev/home/auth-providers/zoho",
  },
  {
    id: OAuthId.Zoom,
    provider_id: OAuthProviderId.Zoom,
    name: "Zoom",
    description: "Authorize tools and agents with Zoom",
    publicIconUrl: `${PUBLIC_ICON_URL}/zoom.svg`,
    icon: Icons.Zoom,
    docs: "https://docs.arcade.dev/home/auth-providers/zoom",
  },
];

/**
 * OAUTH2 PROVIDER - The default OAuth 2.0 provider
 */
export const OAUTH2_PROVIDER = {
  id: OAuthId.OAuth2,
  provider_id: OAuthProviderId.OAuth2,
  name: "OAuth 2.0",
  description:
    "Authorize tools and agents with any OAuth 2.0 compatible provider",
  publicIconUrl: `${PUBLIC_ICON_URL}/oauth.svg`,
  icon: Icons.Oauth,
  docs: "https://docs.arcade.dev/home/auth-providers/oauth2",
};

/**
 * MCP OAUTH2 PROVIDER - The default OAuth 2.0 provider for MCP
 */
export const MCP_OAUTH2_PROVIDER = {
  id: OAuthId.MCP_OAuth2,
  provider_id: OAuthProviderId.MCP_OAuth2,
  name: "MCP",
  description: "Model Context Protocol (MCP)",
  publicIconUrl: `${PUBLIC_ICON_URL}/mcp.svg`,
  icon: Icons.Mcp,
  docs: "https://docs.arcade.dev/en/home/glossary#mcp-server", // TODO: Use the correct docs link when we have it
};

/**
 * OTHER OAUTH PROVIDERS - Generic OAuth providers for custom integrations
 */
export const OTHER_OAUTH_PROVIDERS: OAuthCatalogue[] = [
  OAUTH2_PROVIDER,
  MCP_OAUTH2_PROVIDER,
  {
    id: OAuthId.Salesforce,
    provider_id: OAuthProviderId.Salesforce,
    name: "Salesforce",
    description: "Authorize tools and agents with Salesforce",
    publicIconUrl: `${PUBLIC_ICON_URL}/salesforce.svg`,
    icon: Icons.Salesforce,
    docs: "https://docs.arcade.dev/home/auth-providers/salesforce",
  },
  {
    id: OAuthId.Tesla,
    provider_id: OAuthProviderId.Tesla,
    name: "Tesla",
    description: "Authorize tools and agents with Tesla",
    publicIconUrl: `${PUBLIC_ICON_URL}/tesla.svg`,
    icon: Icons.Tesla,
    docs: "https://docs.arcade.dev/home/auth-providers/tesla",
  },
  {
    id: OAuthId.Zendesk,
    provider_id: OAuthProviderId.Zendesk,
    name: "Zendesk",
    description: "Authorize tools and agents with Zendesk",
    publicIconUrl: `${PUBLIC_ICON_URL}/zendesk.svg`,
    icon: Icons.Zendesk,
    docs: "https://docs.arcade.dev/home/auth-providers/zendesk",
  },
];

/**
 * OAUTH PROVIDERS - A list of all OAuth providers
 */
export const OAUTH_PROVIDERS: OAuthCatalogue[] = [
  ...PREBUILT_OAUTH_PROVIDERS,
  ...OTHER_OAUTH_PROVIDERS,
];

/**
 * OAUTH PROVIDER CATALOGUE - A map of provider IDs to their respective provider
 */
export const OAUTH_PROVIDER_CATALOGUE: Record<string, OAuthCatalogue> =
  OAUTH_PROVIDERS.reduce(
    (acc, provider) => {
      acc[provider.provider_id] = provider;
      return acc;
    },
    {} as Record<string, OAuthCatalogue>
  );
